import { supabase } from '../config/supabase';
import { getApiBaseUrl } from '../utils/apiBase';
import type {
  CreateUserResult,
  Paginated,
  StoreDetail,
  StoreListItem,
  StorePreview,
  UserListItem,
} from '../types/admin';

interface AdminEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) {
    throw new Error('Sesión no válida. Volvé a iniciar sesión.');
  }
  return data.session.access_token;
}

async function adminRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const response = await fetch(`${getApiBaseUrl()}/api/admin${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers ?? {}),
    },
  });

  let payload: AdminEnvelope<T> | null = null;
  try {
    payload = (await response.json()) as AdminEnvelope<T>;
  } catch {
    /* non-json */
  }

  if (!response.ok || !payload?.success) {
    throw new Error(payload?.error || payload?.message || `Error HTTP ${response.status}`);
  }

  return payload.data as T;
}

function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const adminApi = {
  listStores: (filters?: { active?: boolean; q?: string }) =>
    adminRequest<Paginated<StoreListItem>>(`/stores${toQuery(filters ?? {})}`),

  getStore: (storeId: string) => adminRequest<StoreDetail>(`/stores/${storeId}`),

  previewStore: (storeId: string) =>
    adminRequest<StorePreview>(`/boris/stores/${storeId}/preview`),

  createStore: (storeId: string) =>
    adminRequest<{ storeId: string; storeName: string }>('/stores', {
      method: 'POST',
      body: JSON.stringify({ storeId }),
    }),

  updateStore: (storeId: string, patch: { active?: boolean; store_name?: string; country?: string | null }) =>
    adminRequest<{ storeId: string; active?: boolean }>(`/stores/${storeId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  listStoreUsers: (storeId: string) =>
    adminRequest<Paginated<UserListItem>>(`/stores/${storeId}/users`),

  listUsers: (filters?: { storeId?: string; active?: boolean; q?: string }) =>
    adminRequest<Paginated<UserListItem>>(`/users${toQuery(filters ?? {})}`),

  createUser: (body: { email: string; storeId: string; password?: string }) =>
    adminRequest<CreateUserResult>('/users', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  setUserActive: (userId: string, active: boolean) =>
    adminRequest<{ userId: string; active: boolean }>(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    }),

  resetUserPassword: (userId: string, password?: string) =>
    adminRequest<CreateUserResult>(`/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify(password ? { password } : {}),
    }),
};
